import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Icon } from '@/components/ui/icon'
import { useReachableOrganisations } from '@/hooks/queries/shell'
import { errorMessage } from '@/lib/errors'
import {
  authBaseUrl,
  chooseTenant,
  openCookieSettings,
  signOut,
  useSession,
} from '@ventawiz/web-kit'

/**
 * Who is signed in, which organisation they are in — and, with more than
 * one, the switch to another — their account settings, and the way out.
 *
 * Rendered in both the desktop sidebar header and the mobile header, which are
 * separate markup — hence a component rather than inline JSX, so "sign out
 * works on a phone" does not depend on someone remembering to copy it.
 *
 * Renders nothing when there is no session. The shell is only reachable behind
 * the guard, so that case is a cold frame during `restore()` rather than a
 * real state, and an empty trigger is better than one labelled "undefined".
 */
export function UserMenu() {
  const { t } = useTranslation()
  const { session } = useSession()
  const [leaving, setLeaving] = useState(false)
  const [switching, setSwitching] = useState(false)
  // Set by "Cookie settings", read once the menu has closed — see below.
  const openSettingsNext = useRef(false)
  const organisations = useReachableOrganisations(session?.access?.application)

  if (!session) return null

  const name = session.principal.display_name || session.principal.username

  const onSignOut = async () => {
    setLeaving(true)
    // `signOut` clears local state and then re-raises a transport failure.
    // Swallowed here on purpose: by this point the session is gone locally,
    // and letting it escape would leave somebody on a page whose session is
    // already over — signed out everywhere except where they are standing.
    await signOut().catch(() => undefined)
    // A full page navigation to the auth app, not a router push: sign-in is a
    // separate bundle at `/auth` on this origin, so there is no client-side
    // route to it.
    window.location.assign('/auth/login')
  }

  // Switching is a new token for another organisation. The kit's identity
  // change clears the cache, and a fresh load of the app's root drops any
  // page about a store the other organisation does not have.
  const onSwitch = async (handle: string) => {
    setSwitching(true)
    try {
      await chooseTenant(handle)
      window.location.assign('/')
    } catch (error) {
      setSwitching(false)
      toast.error(t('shell.switchFailed', { reason: errorMessage(error) }))
    }
  }
  const choices = organisations.data ?? []

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          // The trigger shows initials, which name nothing to a screen reader.
          aria-label={t('auth.account')}
        >
          {/* The profile avatar (design board 13): 32 round, initials. */}
          <span
            aria-hidden="true"
            className="bg-secondary text-secondary-foreground flex size-8 items-center justify-center rounded-full text-xs font-semibold"
          >
            {initials(name ?? '')}
          </span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-56"
        onCloseAutoFocus={() => {
          // Open the cookie settings only once the menu has handed focus back
          // to its trigger, so the dialog returns focus there when it closes.
          if (!openSettingsNext.current) return
          openSettingsNext.current = false
          window.setTimeout(openCookieSettings, 0)
        }}
      >
        <DropdownMenuLabel className="text-foreground text-sm font-normal tracking-normal normal-case">
          <span className="text-muted-foreground block text-xs">{t('auth.signedInAs')}</span>
          <span className="block truncate text-sm font-semibold">{name}</span>
          {session.access ? (
            <span className="text-muted-foreground block truncate text-xs">
              {session.access.name}
            </span>
          ) : null}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {choices.length > 1 ? (
          <>
            <DropdownMenuLabel className="text-muted-foreground text-xs font-normal tracking-normal normal-case">
              {t(switching ? 'shell.switching' : 'shell.switchOrganisation')}
            </DropdownMenuLabel>
            {choices.map((choice) => {
              const current = choice.slug === session.access?.slug
              return (
                <DropdownMenuItem
                  key={`${choice.application}:${choice.slug}`}
                  disabled={current || switching || !choice.handle}
                  onSelect={() => {
                    if (choice.handle) void onSwitch(choice.handle)
                  }}
                >
                  <Icon name={current ? 'check' : 'corporate_fare'} />
                  <span className="truncate">{choice.name}</span>
                  {current ? <span className="sr-only">({t('shell.current')})</span> : null}
                </DropdownMenuItem>
              )
            })}
            <DropdownMenuSeparator />
          </>
        ) : null}

        <DropdownMenuItem asChild>
          <Link to="/access">
            <Icon name="key" />
            {t('nav.myAccess')}
          </Link>
        </DropdownMenuItem>

        {/* A full page load: account settings are VentaWiz Auth's own UI, at /auth. */}
        <DropdownMenuItem asChild>
          <a href={`${authBaseUrl()}/account`}>
            <Icon name="manage_accounts" />
            {t('shell.accountSettings')}
          </a>
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={() => {
            openSettingsNext.current = true
          }}
        >
          <Icon name="cookie" />
          {t('cookieConsent.settingsLink')}
        </DropdownMenuItem>

        <DropdownMenuItem disabled={leaving} onSelect={() => void onSignOut()}>
          <Icon name="logout" />
          {t('auth.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** "Ana Ortiz" → "AO"; a single name gives its first letter. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = words.length > 1 ? [words[0], words[words.length - 1]] : words
  return letters.map((word) => word?.charAt(0).toUpperCase() ?? '').join('')
}
